import { jsx, jsxs } from "react/jsx-runtime";
import { useSessionStorageState } from "@contember/react-utils";
import classNames from "classnames";
import { useContext, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigationLink } from "../../Navigation.js";
import { usePreventCloseContext } from "../PreventCloseContext.js";
import { DepthContext, useExpandParentContext, ExpandParentContext } from "./Contexts.js";
import { MenuExpandToggle } from "./ExpandToggle.js";
import { MenuLink } from "./MenuLink.js";
import { TAB_INDEX_FOCUSABLE, TAB_INDEX_TEMPORARY_UNFOCUSABLE, TAB_INDEX_NEVER_FOCUSABLE } from "./Types.js";
import { useActiveMenuItemContext } from "./useActiveMenuItem.js";
import { useKeyNavigation } from "./useKeyNavigation.js";
import { useMenuId } from "./useMenuId.js";
import { randomId } from "../../auxiliary/randomId.js";
import { useChildrenAsLabel } from "../../utils/useChildrenAsLabel.js";
import { useComponentClassName } from "../../auxiliary/useComponentClassName.js";
import { Collapsible } from "../Collapsible/Collapsible.js";
import { toStateClass } from "../../utils/toStateClass.js";
import { Label } from "../Typography/Label.js";
function MenuItem({ children, ...props }) {
  const depth = useContext(DepthContext);
  const { isActive, href, navigate } = useNavigationLink(props.to, props.href);
  const id = useRef(`cui-menu-id-${randomId()}`);
  const label = useChildrenAsLabel(props.title);
  const menuItemId = `cui-menu-item-${depth}-${href ?? label}`;
  const componentClassName = useComponentClassName(depth === 0 ? "menu-section" : "menu-group");
  const listItemRef = useRef(null);
  const listItemTitleRef = useRef(null);
  const parentExpandedOnce = useRef(false);
  const { expandParent, parentIsExpanded } = useExpandParentContext();
  useEffect(() => {
    if (parentExpandedOnce.current) {
      return;
    }
    if (isActive) {
      expandParent();
    }
    parentExpandedOnce.current = true;
  }, [isActive, href, expandParent]);
  const hasSubItems = !!children;
  const isInteractive = hasSubItems && depth > 0;
  const activeMenuItem = useActiveMenuItemContext();
  const tabIndex = depth > 0 && hasSubItems || href ? parentIsExpanded ? !activeMenuItem || activeMenuItem === listItemRef.current ? TAB_INDEX_FOCUSABLE : TAB_INDEX_TEMPORARY_UNFOCUSABLE : TAB_INDEX_NEVER_FOCUSABLE : TAB_INDEX_NEVER_FOCUSABLE;
  const menuId = useMenuId();
  const [expanded, setExpanded] = useSessionStorageState(
    `menu-${menuId}-${menuItemId}`,
    (val) => val ?? (props.expandedByDefault || depth === 0 || !label)
  );
  const preventMenuClose = usePreventCloseContext();
  const changeExpand = useCallback((nextExpanded) => {
    if (!isInteractive) {
      return;
    }
    if (listItemRef.current !== document.activeElement) {
      listItemRef.current?.focus();
    }
    setExpanded(nextExpanded);
  }, [isInteractive, setExpanded]);
  const onLabelClick = useCallback((event) => {
    if (event.defaultPrevented) {
      return;
    }
    if (isInteractive && !expanded) {
      preventMenuClose();
    }
    if (navigate) {
      navigate(event);
      changeExpand(true);
    } else {
      changeExpand(!expanded);
    }
    listItemRef.current?.focus();
    event.preventDefault();
  }, [expanded, changeExpand, isInteractive, navigate, preventMenuClose]);
  const onKeyPress = useKeyNavigation({ changeExpand, expanded, depth, isInteractive, listItemRef, onClick: onLabelClick });
  const submenu = useMemo(
    () => {
      const ul = /* @__PURE__ */ jsx(
        "ul",
        {
          "aria-labelledby": isInteractive ? id.current : void 0,
          className: classNames(
            `${componentClassName}-list`,
            hasSubItems && (expanded ? "is-expanded" : "is-collapsed")
          ),
          children
        }
      );
      return isInteractive ? /* @__PURE__ */ jsx(Collapsible, { expanded, children: ul }) : ul;
    },
    [children, componentClassName, expanded, hasSubItems, isInteractive]
  );
  const interactiveProps = isInteractive ? {
    "id": menuItemId,
    "aria-haspopup": true,
    "aria-controls": id.current,
    "aria-expanded": expanded
  } : void 0;
  return /* @__PURE__ */ jsx(DepthContext.Provider, { value: depth + 1, children: /* @__PURE__ */ jsx(ExpandParentContext.Provider, { value: {
    expandParent: useCallback(() => {
      changeExpand(true);
      expandParent();
    }, [changeExpand, expandParent]),
    parentIsExpanded: parentIsExpanded && expanded
  }, children: /* @__PURE__ */ jsxs(
    "li",
    {
      ref: listItemRef,
      ...interactiveProps,
      "aria-label": label,
      role: href ? "link" : void 0,
      className: classNames(
        componentClassName,
        hasSubItems && (expanded ? "is-expanded" : "is-collapsed"),
        toStateClass("interactive", isInteractive),
        toStateClass("active", isActive)
      ),
      onKeyDown: onKeyPress,
      tabIndex,
      "aria-disabled": tabIndex === TAB_INDEX_NEVER_FOCUSABLE,
      children: [
        /* @__PURE__ */ jsxs("div", { ref: listItemTitleRef, className: `${componentClassName}-title`, children: [
          isInteractive && /* @__PURE__ */ jsx(
            MenuExpandToggle,
            {
              checked: expanded,
              controls: id.current,
              disabled: !isInteractive,
              onChange: changeExpand
            }
          ),
          href ? /* @__PURE__ */ jsx(
            MenuLink,
            {
              className: `${componentClassName}-title-content`,
              external: props.external,
              href,
              isActive,
              onClick: onLabelClick,
              suppressTo: expanded,
              children: /* @__PURE__ */ jsx(Label, { className: `${componentClassName}-title-label`, children: props.title })
            }
          ) : /* @__PURE__ */ jsx(
            "span",
            {
              className: `${componentClassName}-title-content`,
              onClick: onLabelClick,
              children: /* @__PURE__ */ jsx(Label, { className: `${componentClassName}-label`, children: props.title })
            }
          )
        ] }),
        submenu
      ]
    }
  ) }) });
}
export {
  MenuItem
};
//# sourceMappingURL=MenuItem.js.map
